import { useState, useRef } from 'react';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { IconButton } from '@mui/material';

/**
 * SettingsText — inline editable text setting.
 * Click preview or edit icon to enter edit mode.
 * Blur or Enter = save, Escape = cancel.
 *
 * @param {string} value - current value
 * @param {function} onChange - called with new value on save
 * @param {number} maxLength - max input length (default 40)
 */
export default function SettingsText({ value, onChange, maxLength = 40 }) {
    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState(value ?? '');
    const inputRef = useRef(null);

    const startEdit = () => {
        setDraft(value ?? '');
        setEditing(true);
        setTimeout(() => inputRef.current?.select(), 0);
    };

    const commit = () => {
        const trimmed = draft.trim();
        if (trimmed && trimmed !== value) onChange(trimmed);
        setEditing(false);
    };

    const cancel = () => {
        setDraft(value ?? '');
        setEditing(false);
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter') commit();
        if (e.key === 'Escape') cancel();
    };

    if (editing) {
        return (
            <input
                ref={inputRef}
                value={draft}
                maxLength={maxLength}
                onChange={(e) => setDraft(e.target.value)}
                onBlur={commit}
                onKeyDown={handleKeyDown}
                autoFocus
                style={{
                    background: '#0d1f2d',
                    border: '1px solid #4cceac',
                    borderRadius: 4,
                    color: '#c8d6e5',
                    fontSize: 13,
                    padding: '2px 8px',
                    outline: 'none',
                    fontFamily: 'inherit',
                    width: 120,
                }}
            />
        );
    }

    return (
        <>
            <span className="settings-preview" style={{ cursor: 'pointer' }} onClick={startEdit}>
                {value || '—'}
            </span>
            <IconButton size="small" onClick={startEdit} sx={{ color: '#294766', '&:hover': { color: '#66b2ff' } }}>
                <EditOutlinedIcon sx={{ fontSize: 14 }} />
            </IconButton>
        </>
    );
}
